import React, { useState, useEffect } from 'react';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';
import useStores from '@stores/useStores';
import { Flex } from '@ui-library/misc';
import Loading from '@ui-library/Loading';
import { MetaData } from '@ui-library/dynamicSelect';
import {
  ResourceSelection,
  ResourceSelectionProps,
  Resource,
} from './resourceSelection/ResourceSelection';
import RemoveResource from './resourceSelection/customResources/RemoveResourceButton';
import EditResourceButton from './resourceSelection/customResources/EditResourceButton';
import CreateResourceButton from './resourceSelection/customResources/CreateResourceButton';
import ColumnSelectionButton from './resourceSelection/columnSelection/ColumnSelectionButton';
import classes from './cloudsource.module.scss';
import { ColumnSelection } from './resourceSelection/columnSelection/ColumnSelectionView';

const forceColumnSelectionBySource: string[] = ['salesforce'];
export interface IResource extends Resource {
  multiple: boolean;
}
export interface IResourceConfig {
  id: string;
  role: string;
  name: string;
  sourceRole: string;
  config?: Record<string, unknown>;
}

interface SelectResourcesProps extends Partial<Omit<ResourceSelectionProps, 'onChange'>> {
  onChange: (
    values: string[] | undefined,
    customResources: IResourceConfig[] | undefined | null,
    columnSelection?: Record<string, ColumnSelection>,
  ) => void;
  customResources?: IResourceConfig[] | undefined | null;
  sourceDefinition: ISourceDefinition;
  metadata: MetaData;
  selectedColumns?: Record<string, ColumnSelection>;
  sourceId?: string;
}

const SelectResources = (props: SelectResourcesProps) => {
  const {
    sourceDefinition,
    onChange,
    onSingleResourceOrLess,
    metadata,
    sourceId,
    selectedResources,
    selectedColumns,
    disabled,
    customResources: cr,
  } = props;
  const { sourceDataStore } = useStores();
  const [resourcesColumns, setResourcesColumns] = useState(selectedColumns);
  const [resources, setResources] = useState<IResource[] | null>(null);
  const [resourceSelection, setResourceSelection] = useState(selectedResources);
  const [loadingResources, setLoadingResources] = useState(true);
  const [loadingCustomResources, setLoadingCustomResources] = useState(!!sourceId);
  const [customResources, setCustomResources] = useState<IResourceConfig[]>(cr || []);
  useEffect(() => {
    (async () => {
      setLoadingResources(true);
      const defResources = await sourceDataStore.getSourceDefResources(
        sourceDefinition.name,
        metadata?.params,
      );
      setResources(defResources);
      setLoadingResources(false);
      if (sourceId) {
        setLoadingCustomResources(true);
        const configResources = await sourceDataStore.getSourceConfigResources(sourceId);
        setCustomResources(configResources);
        setLoadingCustomResources(false);
      }
    })();
  }, []);

  let hideResources = false;
  if (
    !resources ||
    (resources && resources.length < 2) ||
    loadingCustomResources ||
    loadingResources
  ) {
    hideResources = true;
  }
  const creatableResources =
    resources &&
    resources.filter((resource) => resource.multiple).map((resource) => resource.resource);
  let normalResources = resources && resources.filter((resource) => !resource.multiple);
  normalResources =
    normalResources?.map((r) =>
      !r.columns && !forceColumnSelectionBySource.includes(sourceDefinition.name)
        ? r
        : {
            ...r,
            action: (
              <div className={classes.mleft}>
                <ColumnSelectionButton
                  defaultValue={resourcesColumns}
                  resources={normalResources as Resource[]}
                  resourceRole={r.resource}
                  sourceRole={sourceDefinition.name}
                  metadata={metadata}
                  onSubmit={(data) => {
                    setResourcesColumns(data);
                    onChange(resourceSelection, customResources, data);
                  }}
                  disabled={disabled}
                />
              </div>
            ),
          },
    ) || [];
  const customResourcesList =
    (customResources &&
      customResources.map((rsrc) => ({
        ...rsrc,
        resource: rsrc.role,
        id: rsrc.id || `custom-${rsrc.name}`,
        custom: true,
        action: (
          <Flex className={classes.mleft} centered>
            <div className={classes.mright}>
              <EditResourceButton
                sourceId={sourceId}
                resource={rsrc}
                sourceRole={sourceDefinition.name}
                metadata={metadata}
                onSuccess={(data) => {
                  setCustomResources(
                    customResources.map((resource) =>
                      resource.id !== rsrc.id ? resource : { ...resource, ...data },
                    ),
                  );
                }}
                disabled={disabled}
              />
            </div>
            <RemoveResource
              sourceId={sourceId}
              resourceId={rsrc.id}
              onSuccess={() => {
                setCustomResources(customResources.filter((resource) => resource.id !== rsrc.id));
              }}
              disabled={disabled}
            />
          </Flex>
        ),
      }))) ||
    [];
  const displayableResources = [...normalResources, ...customResourcesList];
  const addNewResourceButton = (
    <CreateResourceButton
      sourceId={sourceId}
      metadata={metadata}
      onSuccess={(resource) => {
        setCustomResources([...customResources, resource]);
      }}
      customResources={creatableResources}
      normalResources={normalResources.map((r) => r.resource)}
      sourceRole={sourceDefinition.name}
    />
  );

  return (
    <>
      {hideResources && <Loading />}

      {(!loadingResources && !loadingCustomResources && displayableResources.length > 0 && (
        <div
          style={{ display: (hideResources && 'none') || undefined }}
          className="cloud-resources__container"
        >
          {(!disabled && (
            <Flex justifyContent="flex-end" className={classes.mbottom}>
              {addNewResourceButton}
            </Flex>
          )) ||
            null}
          <ResourceSelection
            selectedResources={selectedResources}
            onSingleResourceOrLess={onSingleResourceOrLess}
            availableResources={displayableResources}
            onChange={(resources) => {
              setResourceSelection(resources);
              onChange(resources, customResources, resourcesColumns);
            }}
            disabled={disabled}
          />
        </div>
      )) ||
        null}
    </>
  );
};

export default SelectResources;
